import React from 'react';
import '@/App.css';
import { BrowserRouter, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import { Toaster } from './components/ui/sonner';
import DashboardLayout from './layouts/DashboardLayout';
import Login from './pages/auth/Login';
import Register from './pages/auth/Register';
import ForgotPassword from './pages/auth/ForgotPassword';
import ResetPassword from './pages/auth/ResetPassword';
import Overview from './pages/dashboard/Overview';
import PaymentLinks from './pages/dashboard/PaymentLinks';
import WhatsAppTemplates from './pages/dashboard/WhatsAppTemplates';
import BulkPaymentLinks from './pages/dashboard/BulkPaymentLinks';
import Transactions from './pages/dashboard/Transactions';
import Withdrawals from './pages/dashboard/Withdrawals';
import BankDetails from './pages/dashboard/BankDetails';
import SettlementPage from './pages/dashboard/Settlement';
import Verification from './pages/dashboard/Verification';
import ApiKeys from './pages/dashboard/ApiKeys';
import Profile from './pages/dashboard/Profile';
import Notifications from './pages/dashboard/Notifications';
import AnalyticsPage from './pages/dashboard/Analytics';
import BudgetOverview from './pages/dashboard/BudgetOverview';
import BudgetTransactions from './pages/dashboard/BudgetTransactions';
import BudgetBudgets from './pages/dashboard/BudgetBudgets';
import BudgetAllocation from './pages/dashboard/BudgetAllocation';
import BudgetLoans from './pages/dashboard/BudgetLoans';
import BudgetCredit from './pages/dashboard/BudgetCredit';
import BudgetExpenseRequests from './pages/dashboard/BudgetExpenseRequests';
import BudgetBranches from './pages/dashboard/BudgetBranches';
import BudgetCashFlow from './pages/dashboard/BudgetCashFlow';
import BudgetReports from './pages/dashboard/BudgetReports';
import BudgetNotifications from './pages/dashboard/BudgetNotifications';
import AdminPanel from './pages/admin/AdminPanel';
import PaymentProviders from './pages/admin/PaymentProviders';
import EmailSettings from './pages/admin/EmailSettings';
import AuditLogs from './pages/admin/AuditLogs';
import SubAdmins from './pages/admin/SubAdmins';
import PackagesAdmin from './pages/admin/Packages';
import SubscriptionsAdmin from './pages/admin/Subscriptions';
import AdminInvoicesPage from './pages/admin/Invoices';
import AdminReceiptsPage from './pages/admin/Receipts';
import AdminStores from './pages/admin/Stores';
import AdminProducts from './pages/admin/Products';
import AdminVcards from './pages/admin/Vcards';
import AdminStoreOrders from './pages/admin/StoreOrders';
import AdminStoreOrderDetail from './pages/admin/StoreOrderDetail';
import AdminCustomDomains from './pages/admin/CustomDomains';
import AdminSettlementBanks from './pages/admin/SettlementBanks';
import AdminCustomers from './pages/admin/Customers';
import AdminPaymentReminders from './pages/admin/PaymentReminders';
import AdminBulkPaymentLinks from './pages/admin/BulkPaymentLinks';
import BudgetManagement from './pages/admin/BudgetManagement';
import BudgetUsers from './pages/admin/BudgetUsers';
import BudgetPackages from './pages/admin/BudgetPackages';
import BudgetUsage from './pages/admin/BudgetUsage';
import BudgetReportsAdmin from './pages/admin/BudgetReports';
import BudgetAlerts from './pages/admin/BudgetAlerts';
import StorePage from './pages/dashboard/Store';
import StoreDomainsPage from './pages/dashboard/CustomDomains';
import RemoveBrandingPage from './pages/dashboard/RemoveBranding';
import VCardPage from './pages/dashboard/VCard';
import PackagePage from './pages/dashboard/Package';
import StoreOrdersPage from './pages/dashboard/StoreOrders';
import StoreOrderDetailPage from './pages/dashboard/StoreOrderDetail';
import PosPage from './pages/dashboard/Pos';
import InvoicesPage from './pages/dashboard/Invoices';
import QuotationsPage from './pages/dashboard/Quotations';
import ReceiptsPage from './pages/dashboard/Receipts';
import CustomersPage from './pages/dashboard/Customers';
import PaymentRemindersPage from './pages/dashboard/PaymentReminders';
import SubscriptionsPage from './pages/dashboard/Subscriptions';
import InstallmentPlansPage from './pages/dashboard/InstallmentPlans';
import BusinessProfilePage from './pages/dashboard/BusinessProfile';
import QRCodesPage from './pages/dashboard/QRCodes';
import StorePublic from './pages/public/StorePublic';
import StoreCheckout from './pages/public/StoreCheckout';
import MarketplaceDirectory from './pages/public/MarketplaceDirectory';
import VCardPublic from './pages/public/VCardPublic';
import BusinessPublic from './pages/public/BusinessPublic';
import CustomDomainRouter from './pages/public/CustomDomainRouter';
import InvoicePublic from './pages/public/InvoicePublic';
import QuotationPublic from './pages/public/QuotationPublic';
import ReceiptPublic from './pages/public/ReceiptPublic';
import Documentation from './pages/dashboard/Documentation';
import Staff from './pages/dashboard/Staff';
import PaymentPage from './pages/public/PaymentPage';
import PaymentCallback from './pages/public/PaymentCallback';
import PackageReceipt from './pages/public/PackageReceipt';
import KycCapture from './pages/public/KycCapture';
import CheckoutBrandHeader from './components/public/CheckoutBrandHeader';
import {
  DASHBOARD_HOST,
  BUSINESS_HOST,
  DOC_HOST,
  LINK_HOST,
  MAIN_HOSTS,
  PAY_HOST,
  SHOP_HOST,
  getDashboardUrl,
  getDocsUrl,
  getPayUrl,
  isDashboardRoute,
  isPublicPaymentRoute,
} from './lib/domains';

function StatusLanding({ title, description, testId }) {
  return (
    <div className="min-h-screen bg-[radial-gradient(circle_at_top,_rgba(16,185,129,0.08),_transparent_34%),linear-gradient(180deg,#f8fafc_0%,#eff6ff_100%)] flex items-center justify-center p-4">
      <div className="w-full max-w-xl space-y-6">
        <CheckoutBrandHeader compact className="pt-2" />
        <div className="bg-white border border-slate-200 shadow-[0_20px_60px_rgba(15,23,42,0.08)] rounded-[28px] p-8 text-center" data-testid={testId}>
          <h1 className="text-xl font-semibold text-slate-900 mb-2" style={{ fontFamily: 'Outfit, sans-serif' }}>{title}</h1>
          <p className="text-sm text-slate-500">{description}</p>
        </div>
      </div>
    </div>
  );
}

function DomainRouteGuard() {
  const location = useLocation();

  React.useEffect(() => {
    const hostname = window.location.hostname;
    const { pathname, search, hash } = location;
    const isPremiumHost = hostname === SHOP_HOST || hostname === LINK_HOST || hostname === BUSINESS_HOST;

    if (hostname === DOC_HOST && (pathname === '/' || pathname.startsWith('/documentation') || pathname.startsWith('/docs'))) {
      return;
    }

    if (isPremiumHost) {
      return;
    }

    if ((hostname === PAY_HOST || hostname === DOC_HOST || MAIN_HOSTS.has(hostname)) && isDashboardRoute(pathname)) {
      window.location.replace(getDashboardUrl(pathname, search, hash));
      return;
    }

    if ((hostname === DASHBOARD_HOST || hostname === DOC_HOST || MAIN_HOSTS.has(hostname)) && isPublicPaymentRoute(pathname)) {
      window.location.replace(getPayUrl(pathname, search, hash));
    }
  }, [location]);

  return null;
}

function ExternalRedirect({ to }) {
  React.useEffect(() => {
    window.location.replace(to);
  }, [to]);
  return null;
}

function PaymentRouteWrapper({ children }) {
  return children;
}

function ProtectedRoute({ children, adminOnly }) {
  const { user, loading } = useAuth();
  if (loading) {
    return (
      <div style={{
        position: 'fixed', inset: 0, display: 'flex', flexDirection: 'column',
        alignItems: 'center', justifyContent: 'center',
        background: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 40%, #f8fafc 100%)',
        zIndex: 9999,
      }}>
        {/* Animated logo mark */}
        <div style={{ position: 'relative', marginBottom: 32 }}>
          {/* Outer ring */}
          <div style={{
            position: 'absolute', inset: -16,
            borderRadius: '50%',
            border: '2px solid rgba(16,185,129,0.15)',
            animation: 'jjuma-ring-out 2s linear infinite',
          }} />
          {/* Spinner ring */}
          <div style={{
            width: 64, height: 64, borderRadius: '50%',
            border: '3px solid #d1fae5',
            borderTopColor: '#059669',
            animation: 'jjuma-spin 0.8s linear infinite',
          }} />
          {/* Centre logo badge */}
          <div style={{
            position: 'absolute', inset: 0, display: 'flex',
            alignItems: 'center', justifyContent: 'center',
          }}>
            <div style={{
              width: 40, height: 40, borderRadius: 12,
              background: 'linear-gradient(135deg, #059669, #10b981)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              boxShadow: '0 4px 16px rgba(5,150,105,0.35)',
              animation: 'jjuma-pulse 2s ease-in-out infinite',
            }}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5" stroke="white" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
              </svg>
            </div>
          </div>
        </div>

        {/* Brand text */}
        <h1 style={{
          fontFamily: "'Outfit', 'Inter', sans-serif",
          fontSize: 22, fontWeight: 700,
          color: '#065f46', marginBottom: 6, letterSpacing: '-0.3px',
        }}>Jjuma Pay</h1>
        <p style={{ fontSize: 13, color: '#6b7280', marginBottom: 28 }}>Loading...</p>

        {/* Animated dots */}
        <div style={{ display: 'flex', gap: 6 }}>
          {[0, 1, 2].map(i => (
            <div key={i} style={{
              width: 7, height: 7, borderRadius: '50%',
              background: '#10b981',
              animation: `jjuma-dot 1.2s ease-in-out ${i * 0.2}s infinite`,
            }} />
          ))}
        </div>

        {/* Keyframes injected via style tag */}
        <style>{`
          @keyframes jjuma-spin {
            to { transform: rotate(360deg); }
          }
          @keyframes jjuma-pulse {
            0%, 100% { transform: scale(1); box-shadow: 0 4px 16px rgba(5,150,105,0.35); }
            50% { transform: scale(1.08); box-shadow: 0 8px 24px rgba(5,150,105,0.5); }
          }
          @keyframes jjuma-ring-out {
            from { transform: scale(0.85); opacity: 0.6; }
            to { transform: scale(1.3); opacity: 0; }
          }
          @keyframes jjuma-dot {
            0%, 80%, 100% { transform: scale(0.6); opacity: 0.4; }
            40% { transform: scale(1.1); opacity: 1; }
          }
        `}</style>
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (adminOnly && user.role !== 'admin') return <Navigate to="/dashboard" replace />;
  return children;
}

function PublicRoute({ children }) {
  const { user } = useAuth();
  if (user) return <Navigate to="/dashboard" replace />;
  return children;
}

function App() {
  const hostname = typeof window !== 'undefined' ? window.location.hostname : '';
  const isDocsHost = hostname === DOC_HOST;
  const isShopHost = hostname === SHOP_HOST;
  const isLinkHost = hostname === LINK_HOST;
  const isBusinessHost = hostname === BUSINESS_HOST;
  const isKnownPublicHost = [DASHBOARD_HOST, PAY_HOST, SHOP_HOST, LINK_HOST, BUSINESS_HOST, DOC_HOST].includes(hostname) || MAIN_HOSTS.has(hostname) || hostname === 'localhost' || hostname === '127.0.0.1';
  const isCustomPublicHost = !isKnownPublicHost;
  return (
    <AuthProvider>
      <BrowserRouter>
        <DomainRouteGuard />
        <Routes>
          {isDocsHost ? (
            <>
              <Route path="/" element={<Documentation standalone />} />
              <Route path="/documentation" element={<Documentation standalone />} />
              <Route path="*" element={<Documentation standalone />} />
            </>
          ) : isCustomPublicHost ? (
            <Route path="*" element={<CustomDomainRouter />} />
          ) : (
            <>
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><Register /></PublicRoute>} />
              <Route path="/forgot-password" element={<ForgotPassword />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/p/:slug" element={<PaymentRouteWrapper><PaymentPage /></PaymentRouteWrapper>} />
              <Route path="/pay/:slug" element={<PaymentRouteWrapper><PaymentPage /></PaymentRouteWrapper>} />
              <Route path="/checkout/:slug" element={<PaymentRouteWrapper><PaymentPage /></PaymentRouteWrapper>} />
              <Route path="/invoice/:id" element={<InvoicePublic />} />
              <Route path="/quotation/:id" element={<QuotationPublic />} />
              <Route path="/receipt/:id" element={<ReceiptPublic />} />
              <Route path="/receipt/package" element={<PackageReceipt />} />
              <Route path="/success" element={<StatusLanding title="Payment Successful" description="Your payment has been completed successfully." testId="public-success-page" />} />
              <Route path="/failed" element={<StatusLanding title="Payment Failed" description="Your payment could not be completed." testId="public-failed-page" />} />
              <Route path="/cancelled" element={<StatusLanding title="Payment Cancelled" description="The payment was cancelled." testId="public-cancelled-page" />} />
              <Route path="/payment-success" element={<StatusLanding title="Payment Successful" description="Your payment has been completed successfully." testId="public-legacy-success-page" />} />
              <Route path="/payment-failed" element={<StatusLanding title="Payment Failed" description="Your payment could not be completed." testId="public-legacy-failed-page" />} />
              <Route path="/payment-cancelled" element={<StatusLanding title="Payment Cancelled" description="The payment was cancelled." testId="public-legacy-cancelled-page" />} />
              <Route path="/pay/:slug/callback" element={<PaymentCallback />} />
              <Route path="/kyc/capture/:token" element={<KycCapture />} />
              {isShopHost ? (
                <>
                  <Route path="/" element={<MarketplaceDirectory />} />
                  <Route path="/directory" element={<MarketplaceDirectory />} />
                  <Route path="/directory/category/:categorySlug" element={<MarketplaceDirectory />} />
                  <Route path="/directory/location/:locationSlug" element={<MarketplaceDirectory />} />
                  <Route path="/:username" element={<StorePublic />} />
                  <Route path="/:username/product/:productSlug" element={<StorePublic />} />
                  <Route path="/:username/checkout" element={<StoreCheckout />} />
                  <Route path="*" element={<StorePublic />} />
                </>
              ) : null}
              {isLinkHost ? (
                <>
                  <Route path="/" element={<VCardPublic />} />
                  <Route path="/:username" element={<VCardPublic />} />
                  <Route path="*" element={<VCardPublic />} />
                </>
              ) : null}
              {isBusinessHost ? (
                <>
                  <Route path="/" element={<BusinessPublic />} />
                  <Route path="/:username" element={<BusinessPublic />} />
                  <Route path="*" element={<BusinessPublic />} />
                </>
              ) : null}
              <Route path="/dashboard" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Overview />} />
                <Route path="analytics" element={<AnalyticsPage />} />
                <Route path="budget" element={<BudgetOverview />} />
                <Route path="budget/transactions" element={<BudgetTransactions />} />
                <Route path="budget/expense-requests" element={<BudgetExpenseRequests />} />
                <Route path="budget/branches" element={<BudgetBranches />} />
                <Route path="budget/budgets" element={<BudgetBudgets />} />
                <Route path="budget/allocation" element={<BudgetAllocation />} />
                <Route path="budget/loans" element={<BudgetLoans />} />
                <Route path="budget/loans/:id" element={<BudgetLoans />} />
                <Route path="budget/credit" element={<BudgetCredit />} />
                <Route path="budget/cash-flow" element={<BudgetCashFlow />} />
                <Route path="budget/reports" element={<BudgetReports />} />
                <Route path="budget/notifications" element={<BudgetNotifications />} />
                <Route path="payment-links" element={<PaymentLinks />} />
                <Route path="whatsapp-templates" element={<WhatsAppTemplates />} />
                <Route path="bulk-payment-links" element={<BulkPaymentLinks />} />
                <Route path="transactions" element={<Transactions />} />
                <Route path="withdrawals" element={<Withdrawals />} />
                <Route path="bank-details" element={<BankDetails />} />
                <Route path="settlement" element={<SettlementPage />} />
                <Route path="verification" element={<Verification />} />
                <Route path="api-keys" element={<ApiKeys />} />
                <Route path="customers" element={<CustomersPage />} />
                <Route path="customers/:customerId" element={<CustomersPage />} />
                <Route path="invoices" element={<InvoicesPage />} />
                <Route path="quotations" element={<QuotationsPage />} />
                <Route path="receipts" element={<ReceiptsPage />} />
                <Route path="reminders" element={<PaymentRemindersPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="installments" element={<InstallmentPlansPage />} />
                <Route path="business-profile" element={<BusinessProfilePage />} />
                <Route path="qr-codes" element={<QRCodesPage />} />
                <Route path="package" element={<PackagePage />} />
                <Route path="store" element={<StorePage />} />
                <Route path="store/products" element={<StorePage />} />
                <Route path="store/settings" element={<StorePage />} />
                <Route path="store/domains" element={<StoreDomainsPage pageTitle="Store Domains" pageDescription="Connect a custom domain to your Jjuma Store with simple DNS verification." />} />
                <Route path="custom-domains" element={<StoreDomainsPage pageTitle="Custom Domains" pageDescription="Manage custom domains, verification and DNS instructions for your Jjuma storefronts." />} />
                <Route path="delivery-options" element={<StorePage />} />
                <Route path="marketplace" element={<StorePage />} />
                <Route path="remove-branding" element={<RemoveBrandingPage />} />
                <Route path="vcard" element={<VCardPage />} />
                <Route path="store/orders" element={<StoreOrdersPage />} />
                <Route path="store/orders/:orderId" element={<StoreOrderDetailPage />} />
                <Route path="customers/:customerId" element={<CustomersPage crmMode />} />
                <Route path="crm" element={<CustomersPage crmMode />} />
                <Route path="crm/:customerId" element={<CustomersPage crmMode />} />
                <Route path="pos" element={<PosPage />} />
                <Route path="documentation" element={<ExternalRedirect to={getDocsUrl('/')} />} />
                <Route path="staff" element={<Staff />} />
                <Route path="team" element={<Staff />} />
                <Route path="profile" element={<Profile />} />
                <Route path="notifications" element={<Notifications />} />
              </Route>
              <Route path="/account" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Profile />} />
              </Route>
              <Route path="/settings" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Profile />} />
              </Route>
              <Route path="/kyc" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Verification />} />
              </Route>
              <Route path="/transactions" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Transactions />} />
              </Route>
              <Route path="/withdrawals" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<Withdrawals />} />
              </Route>
              <Route path="/payment-links" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<PaymentLinks />} />
              </Route>
              <Route path="/api-keys" element={<ProtectedRoute><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<ApiKeys />} />
              </Route>
              <Route path="/admin" element={<ProtectedRoute adminOnly><DashboardLayout /></ProtectedRoute>}>
                <Route index element={<AdminPanel />} />
                <Route path="budget-management" element={<BudgetManagement />} />
                <Route path="users" element={<AdminPanel defaultTab="users" />} />
                <Route path="verifications" element={<AdminPanel defaultTab="verifications" />} />
                <Route path="providers" element={<PaymentProviders />} />
                <Route path="settlement-banks" element={<AdminSettlementBanks />} />
                <Route path="packages" element={<PackagesAdmin />} />
                <Route path="subscriptions" element={<SubscriptionsAdmin />} />
                <Route path="invoices" element={<AdminInvoicesPage />} />
                <Route path="receipts" element={<AdminReceiptsPage />} />
                <Route path="stores" element={<AdminStores />} />
                <Route path="products" element={<AdminProducts />} />
                <Route path="vcards" element={<AdminVcards />} />
                <Route path="store-orders" element={<AdminStoreOrders />} />
                <Route path="store-orders/:orderId" element={<AdminStoreOrderDetail />} />
                <Route path="payment-reminders" element={<AdminPaymentReminders />} />
                <Route path="bulk-payment-links" element={<AdminBulkPaymentLinks />} />
                <Route path="custom-domains" element={<AdminCustomDomains />} />
                <Route path="customers" element={<AdminCustomers />} />
                <Route path="email" element={<EmailSettings />} />
                <Route path="audit-logs" element={<AuditLogs />} />
                <Route path="sub-admins" element={<SubAdmins />} />
                <Route path="budget-users" element={<BudgetUsers />} />
                <Route path="budget-packages" element={<BudgetPackages />} />
                <Route path="budget-usage" element={<BudgetUsage />} />
                <Route path="budget-reports" element={<BudgetReportsAdmin />} />
                <Route path="budget-alerts" element={<BudgetAlerts />} />
                <Route path="commissions" element={<AdminPanel defaultTab="commissions" />} />
                <Route path="settings" element={<AdminPanel defaultTab="settings" />} />
              </Route>
              <Route path="/" element={isDocsHost ? <Documentation standalone /> : <Navigate to="/dashboard" replace />} />
              <Route path="*" element={isDocsHost ? <Documentation standalone /> : <Navigate to="/dashboard" replace />} />
            </>
          )}
        </Routes>
      </BrowserRouter>
      <Toaster position="top-right" />
    </AuthProvider>
  );
}

export default App;
